import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Button, Fab, Stack, useTheme, Chip, Tooltip } from '@mui/material';
import Calendar from './Calendar';
import AppointmentDetail from './AppointmentDetail';
import AppointmentEditDialog from './AppointmentEditDialog';
import AppointmentForm from './AppointmentForm';
import UnavailableBlockDialog from './UnavailableBlockDialog';
import UnavailableBlockEditDialog from './UnavailableBlockEditDialog';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import PersonIcon from '@mui/icons-material/Person';
import { getAppointments, getUnavailableBlocks, getPractitioners, getAppointmentTypes, appointmentChangeEvent } from '../../api/calendarService';
import { CalendarEvent, Appointment, UnavailableBlock, appointmentToCalendarEvent, unavailableBlockToCalendarEvent, practitionerColors } from '../../models/calendar';

// Mock data for appointments until API integration is complete
const mockAppointments = [
  { id: 1, patientName: 'John Doe', time: '09:00', duration: 30 },
  { id: 2, patientName: 'Jane Smith', time: '10:30', duration: 45 },
  { id: 3, patientName: 'Alice Johnson', time: '13:15', duration: 60 },
];

// For backend API integration testing - to be removed later
const useMockData = false; // Using real Cliniko data through API endpoints

interface AppointmentCalendarProps {
  practitionerId?: string;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ practitionerId }) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAppointmentFormOpen, setIsAppointmentFormOpen] = useState<boolean>(false);
  const [isUnavailableBlockFormOpen, setIsUnavailableBlockFormOpen] = useState<boolean>(false);
  const [isEditBlockDialogOpen, setIsEditBlockDialogOpen] = useState<boolean>(false);
  const [selectedUnavailableBlock, setSelectedUnavailableBlock] = useState<UnavailableBlock | null>(null);
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<any[]>([]);

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  // Handle month change
  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
  };
  
  // Handle opening the appointment form
  const handleOpenAppointmentForm = () => {
    setIsAppointmentFormOpen(true);
  };
  
  // Handle closing the appointment form
  const handleCloseAppointmentForm = () => {
    setIsAppointmentFormOpen(false);
  };
  
  // Handle opening the unavailable block form - now directly opens week view
  const handleOpenUnavailableBlockForm = () => {
    setIsUnavailableBlockFormOpen(true);
  };
  
  // Handle closing the unavailable block form
  const handleCloseUnavailableBlockForm = () => {
    setIsUnavailableBlockFormOpen(false);
  };

  // Handle closing the unavailable block edit dialog
  const handleCloseEditBlockDialog = () => {
    setIsEditBlockDialogOpen(false);
    setSelectedUnavailableBlock(null);
  };

  // Convert CalendarEvent to UnavailableBlock
  const calendarEventToUnavailableBlock = (event: CalendarEvent): UnavailableBlock => {
    return {
      id: event.id,
      practitioner_id: event.practitionerId,
      practitioner_name: event.practitionerName,
      starts_at: event.start.toISOString(),
      ends_at: event.end.toISOString(),
      notes: event.notes,
      unavailable_block_type_id: undefined
    };
  };

  // State for appointment editing
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarEvent | null>(null);
  const [isAppointmentEditDialogOpen, setIsAppointmentEditDialogOpen] = useState<boolean>(false);

  // Handle calendar event click (for appointments and unavailable blocks)
  const handleEventClick = (event: CalendarEvent) => {
    if (event.type === 'unavailable') {
      // Open the unavailable block edit dialog
      setSelectedUnavailableBlock(calendarEventToUnavailableBlock(event));
      setIsEditBlockDialogOpen(true);
    } else if (event.type === 'appointment') {
      // Set the selected date to the appointment date
      setSelectedDate(new Date(event.start));
      
      // Open the appointment edit dialog directly
      setSelectedAppointment(event);
      setIsAppointmentEditDialogOpen(true);
    }
  };
  
  // Handle appointment or block creation/update/deletion
  const handleCalendarUpdated = () => {
    // Refresh the calendar data and practitioners
    fetchCalendarData();
    // Also refresh practitioners in case a new one was added in Cliniko
    fetchPractitioners();
  };
  
  // Function to fetch practitioners
  const fetchPractitioners = async () => {
    try {
      console.log('Refreshing practitioners list...');
      
      // Fetch practitioners
      const practitionersResponse = await getPractitioners();
      
      if (practitionersResponse.data && practitionersResponse.data.practitioners) {
        const formattedPractitioners = practitionersResponse.data.practitioners.map((p: any) => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`
        }));
        console.log('Refreshed practitioners:', formattedPractitioners);
        setPractitioners(formattedPractitioners);
      } else if (practitionersResponse.practitioners) {
        // Handle case where data property isn't present
        const formattedPractitioners = practitionersResponse.practitioners.map((p: any) => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`
        }));
        console.log('Refreshed practitioners (alt):', formattedPractitioners);
        setPractitioners(formattedPractitioners);
      }
    } catch (err) {
      console.error('Error fetching practitioners:', err);
    }
  };
  
  // Fetch calendar data function (extracted from useEffect)
  const fetchCalendarData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Calculate first and last day of the visible calendar (includes prev/next month days)
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      
      // Get the last day of the month for the end date (last day of next month to include overflow days)
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
      
      // Adjust to get Sunday of the first week
      const firstDayOfWeek = firstDay.getDay();
      firstDay.setDate(firstDay.getDate() - firstDayOfWeek);
      
      // Adjust to get Saturday of the last week
      const lastDayOfWeek = 6 - lastDay.getDay();
      lastDay.setDate(lastDay.getDate() + lastDayOfWeek);
      
      console.log('Calendar date range:', { 
        first: firstDay.toLocaleDateString('en-AU'), 
        last: lastDay.toLocaleDateString('en-AU'),
        currentMonth: currentMonth.getMonth() + 1
      });
      
      // Format dates for API
      const startDate = firstDay.toISOString();
      const endDate = lastDay.toISOString();
      
      // Fetch appointments for all practitioners unless specified
      const appointmentsPromise = getAppointments({
        startDate,
        endDate,
        // Only filter by practitioner if explicitly provided as a prop
        ...(practitionerId ? { practitionerId } : {})
      });
      
      // Always fetch unavailable blocks for all practitioners since they're specific to each practitioner
      // This ensures we see unavailable blocks for each practitioner in the calendar
      const unavailableBlocksPromise = getUnavailableBlocks({
        startDate,
        endDate
        // No practitioner filter - we want to see blocks for all practitioners in the calendar
      });
      
      // Wait for both requests to complete
      const [appointmentsResponse, unavailableBlocksResponse] = await Promise.all([
        appointmentsPromise,
        unavailableBlocksPromise
      ]);
      
      // Convert to calendar events
      const appointmentEvents = appointmentsResponse.appointments.map(
        (appointment: Appointment) => appointmentToCalendarEvent(appointment)
      );
      
      // Process unavailable blocks correctly
      // Keep only the ones with valid practitioner IDs and filter out duplicates
      console.log(`Processing ${unavailableBlocksResponse.unavailable_blocks.length} unavailable blocks from Cliniko`);
      
      // Map unavailable blocks to calendar events, but only those with valid practitioner data
      const allUnavailableBlockEvents = unavailableBlocksResponse.unavailable_blocks
        .filter(block => {
          // Make sure we have a valid practitioner ID
          return block.practitioner_id && block.practitioner_name;
        })
        .map(block => unavailableBlockToCalendarEvent(block));
      
      console.log(`Found ${allUnavailableBlockEvents.length} valid unavailable blocks`);
      
      // Create a unique key for each block based on practitioner + start + end time
      const uniqueBlockKeys = new Set();
      const unavailableBlockEvents = allUnavailableBlockEvents.filter(block => {
        const startTime = block.start.toISOString();
        const endTime = block.end.toISOString();
        const key = `${block.practitionerId}-${startTime}-${endTime}`;
        
        // If we've seen this key before, skip it
        if (uniqueBlockKeys.has(key)) {
          console.log(`Skipping duplicate unavailable block: ${key}`);
          return false;
        }
        
        // Otherwise, add it to our set and keep the block
        uniqueBlockKeys.add(key);
        return true;
      });
      
      console.log(`Total unavailable blocks after deduplication: ${unavailableBlockEvents.length}`);
      
      // Combine all events
      setCalendarEvents([...appointmentEvents, ...unavailableBlockEvents]);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to load calendar data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch practitioners and appointment types
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        console.log('Fetching practitioners and appointment types...');
        
        // Fetch practitioners
        const practitionersResponse = await getPractitioners();
        console.log('Raw practitioners response:', practitionersResponse);
        
        if (practitionersResponse.data && practitionersResponse.data.practitioners) {
          const formattedPractitioners = practitionersResponse.data.practitioners.map((p: any) => ({
            id: p.id,
            name: `${p.first_name} ${p.last_name}`
          }));
          console.log('Formatted practitioners:', formattedPractitioners);
          setPractitioners(formattedPractitioners);
        } else if (practitionersResponse.practitioners) {
          // Handle case where data property isn't present
          const formattedPractitioners = practitionersResponse.practitioners.map((p: any) => ({
            id: p.id,
            name: `${p.first_name} ${p.last_name}`
          }));
          console.log('Formatted practitioners (alt):', formattedPractitioners);
          setPractitioners(formattedPractitioners);
        } else {
          console.error('Unexpected practitioners response structure:', practitionersResponse);
          
          // Set default practitioners as a fallback
          const defaultPractitioners = [
            { id: '1', name: 'Dr. Smith' },
            { id: '2', name: 'Dr. Johnson' }
          ];
          console.log('Using default practitioners:', defaultPractitioners);
          setPractitioners(defaultPractitioners);
        }
        
        // Fetch appointment types
        const appointmentTypesResponse = await getAppointmentTypes();
        console.log('Raw appointment types response:', appointmentTypesResponse);
        
        if (appointmentTypesResponse.data && appointmentTypesResponse.data.appointment_types) {
          const formattedTypes = appointmentTypesResponse.data.appointment_types.map((t: any) => ({
            id: t.id,
            name: t.name
          }));
          console.log('Formatted appointment types:', formattedTypes);
          setAppointmentTypes(formattedTypes);
        } else if (appointmentTypesResponse.appointment_types) {
          // Handle case where 'data' property isn't present
          const formattedTypes = appointmentTypesResponse.appointment_types.map((t: any) => ({
            id: t.id,
            name: t.name
          }));
          console.log('Formatted appointment types (alt):', formattedTypes);
          setAppointmentTypes(formattedTypes);
        } else {
          console.error('Unexpected appointment types response structure:', appointmentTypesResponse);
          
          // Add some default appointment types as a fallback
          const defaultTypes = [
            { id: '1194097000748949287', name: 'IV Drip Appointment' },
            { id: '1092387684841692462', name: 'Initial Consultation' },
            { id: '1147115227389631491', name: 'Follow-up Appointment' }
          ];
          console.log('Using default appointment types:', defaultTypes);
          setAppointmentTypes(defaultTypes);
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
        setError('Failed to load practitioners and appointment types');
        
        // Add some default appointment types as a fallback
        const defaultTypes = [
          { id: '1194097000748949287', name: 'IV Drip Appointment' },
          { id: '1092387684841692462', name: 'Initial Consultation' },
          { id: '1147115227389631491', name: 'Follow-up Appointment' }
        ];
        console.log('Using default appointment types due to error:', defaultTypes);
        setAppointmentTypes(defaultTypes);
      }
    };
    
    fetchMetadata();
  }, []);

  // Use a ref to keep the latest version of fetchCalendarData available to event listeners
  const fetchCalendarDataRef = useRef(fetchCalendarData);
  
  // Update the ref when fetchCalendarData dependencies change
  useEffect(() => {
    fetchCalendarDataRef.current = fetchCalendarData;
  }, [currentMonth, practitionerId]);
  
  // Listen for appointment change events
  useEffect(() => {
    // Handler for all appointment change events 
    const handleAppointmentChange = () => {
      console.log('Appointment change detected - refreshing calendar data');
      fetchCalendarDataRef.current();
    };
    
    // Add event listeners for all appointment change types
    appointmentChangeEvent.addEventListener('appointment-created', handleAppointmentChange);
    appointmentChangeEvent.addEventListener('appointment-updated', handleAppointmentChange);
    appointmentChangeEvent.addEventListener('appointment-cancelled', handleAppointmentChange);
    
    // Clean up listeners on component unmount
    return () => {
      appointmentChangeEvent.removeEventListener('appointment-created', handleAppointmentChange);
      appointmentChangeEvent.removeEventListener('appointment-updated', handleAppointmentChange);
      appointmentChangeEvent.removeEventListener('appointment-cancelled', handleAppointmentChange);
    };
  }, []); // Only run once on mount
  
  // Fetch calendar data when current month changes
  useEffect(() => {
    if (useMockData) {
      // For development - generate some mock data for the current month
      const mockEvents: CalendarEvent[] = [];
      
      // Generate some appointments on Mondays and Wednesdays
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      for (let day = firstDay.getDate(); day <= lastDay.getDate(); day++) {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        
        // Add appointments on Mondays and Wednesdays
        if (date.getDay() === 1 || date.getDay() === 3) {
          mockEvents.push({
            id: `mock-event-${day}-1`,
            title: 'John Doe',
            start: new Date(date.setHours(9, 0, 0, 0)),
            end: new Date(date.setHours(9, 30, 0, 0)),
            type: 'appointment',
            practitionerId: '1',
            patientName: 'John Doe',
            color: '#4caf50'
          });
          
          // Reset date hours that were modified by the setHours method
          date.setDate(day);
          
          mockEvents.push({
            id: `mock-event-${day}-2`,
            title: 'Jane Smith',
            start: new Date(date.setHours(10, 30, 0, 0)),
            end: new Date(date.setHours(11, 15, 0, 0)),
            type: 'appointment',
            practitionerId: '1',
            patientName: 'Jane Smith',
            color: '#4caf50'
          });
        }
        
        // Add unavailable blocks on Fridays
        if (date.getDay() === 5) {
          date.setDate(day); // Reset date
          mockEvents.push({
            id: `mock-unavailable-${day}`,
            title: 'Unavailable',
            start: new Date(date.setHours(14, 0, 0, 0)),
            end: new Date(date.setHours(17, 0, 0, 0)),
            type: 'unavailable',
            practitionerId: '1',
            color: '#f44336'
          });
        }
      }
      
      setCalendarEvents(mockEvents);
      return;
    }
    
    // For production - fetch real data from API
    fetchCalendarData();
  }, [currentMonth, practitionerId]);

  // Get appointments for selected date
  const getAppointmentsForDate = (date: Date | null) => {
    if (!date) return [];
    
    if (useMockData) {
      // Use mock data for development
      if (date.getDay() === 1 || date.getDay() === 3) { // Monday or Wednesday
        return mockAppointments;
      }
      return [];
    }
    
    // Return calendar events for the selected date
    return calendarEvents;
  };

  return (
    <Box sx={{ mt: 3, position: 'relative' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.main',
        color: 'primary.contrastText',
        p: 2,
        borderRadius: 2,
        boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarMonthIcon sx={{ fontSize: 32, mr: 2 }} />
          <Typography variant="h5" component="h2">
            Appointment Calendar
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button 
              variant="contained" 
              color="success"
              startIcon={<AddIcon />}
              onClick={handleOpenAppointmentForm}
              sx={{ 
                fontWeight: 'bold',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : undefined
              }}
            >
              New Appointment
            </Button>
            
            <Button 
              variant="contained" 
              color="error"
              startIcon={<BlockIcon />}
              onClick={handleOpenUnavailableBlockForm}
              sx={{ 
                fontWeight: 'bold',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : undefined
              }}
            >
              Unavailable Time
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={() => {
                fetchCalendarData();
                fetchPractitioners();
              }}
              disabled={loading}
              sx={{ 
                fontWeight: 'bold',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : undefined
              }}
            >
              Refresh Calendar
            </Button>
          </Stack>
          
          {loading && (
            <CircularProgress 
              size={24} 
              sx={{ color: 'primary.contrastText' }} 
            />
          )}
        </Box>
      </Box>
      
      {error && (
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: 'error.light', 
          color: 'error.contrastText',
          borderRadius: 2
        }}>
          <Typography variant="body1">{error}</Typography>
        </Box>
      )}
      
      <Paper 
        elevation={4}
        sx={{ 
          p: 2, 
          borderRadius: 3,
          backgroundImage: 'linear-gradient(to bottom, rgba(240, 245, 255, 0.8), rgba(255, 255, 255, 1))'
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Calendar 
              onDateSelect={handleDateSelect} 
              onMonthChange={handleMonthChange}
              events={calendarEvents}
              onEventClick={handleEventClick}
            />
          </Grid>
          
          {/* Practitioner Color Legend */}
          <Grid item xs={12}>
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'background.paper', 
              borderRadius: 1,
              boxShadow: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ mr: 2 }}>
                <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Practitioners:
              </Typography>
              {practitioners && practitioners.length > 0 ? (
                practitioners.map((practitioner, index) => {
                  const practitionerId = practitioner.id;
                  const colorIndex = practitionerId ? 
                    Math.abs(practitionerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % practitionerColors.length : 
                    index;
                  const color = practitionerColors[colorIndex];
                  
                  return (
                    <Tooltip key={practitioner.id} title={`Appointments for ${practitioner.name}`}>
                      <Chip 
                        icon={<PersonIcon />}
                        label={practitioner.name}
                        sx={{ 
                          bgcolor: color,
                          color: 'white',
                          fontWeight: 'medium',
                          '& .MuiChip-icon': { color: 'white' }
                        }}
                      />
                    </Tooltip>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No practitioners found. Please add practitioners in the admin panel.
                </Typography>
              )}
              <Tooltip title="Cancelled appointments">
                <Chip 
                  label="Cancelled"
                  sx={{ bgcolor: '#9e9e9e', color: 'white' }}
                />
              </Tooltip>
              <Tooltip title="Unavailable time blocks (practitioner-specific)">
                <Chip 
                  label="Unavailable Time"
                  sx={{ bgcolor: '#f44336', color: 'white' }}
                />
              </Tooltip>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <AppointmentDetail 
              selectedDate={selectedDate} 
              events={getAppointmentsForDate(selectedDate)}
              practitioners={practitioners}
              appointmentTypes={appointmentTypes}
              onAppointmentUpdated={fetchCalendarData}
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ 
        mt: 3, 
        mb: 2, 
        p: 2, 
        borderLeft: '4px solid #3f51b5',
        bgcolor: 'rgba(63, 81, 181, 0.08)'
      }}>
        <Typography variant="body2" color="text.secondary">
          Appointments and unavailable blocks are shown in real-time.
        </Typography>
      </Box>
      
      {/* Floating action button for mobile */}
      <Fab 
        color="primary" 
        aria-label="add" 
        onClick={handleOpenAppointmentForm}
        sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24,
          display: { xs: 'flex', md: 'none' }
        }}
      >
        <AddIcon />
      </Fab>
      
      {/* Appointment creation form */}
      <AppointmentForm 
        open={isAppointmentFormOpen}
        onClose={handleCloseAppointmentForm}
        selectedDate={selectedDate}
        practitionerId={practitionerId}
        onAppointmentCreated={handleCalendarUpdated}
      />
      
      {/* Unavailable block creation form */}
      <UnavailableBlockDialog
        open={isUnavailableBlockFormOpen}
        onClose={handleCloseUnavailableBlockForm}
        selectedDate={selectedDate}
        practitionerId={practitionerId}
        practitioners={practitioners}
        onBlockCreated={handleCalendarUpdated}
      />

      {/* Unavailable block edit dialog */}
      <UnavailableBlockEditDialog
        open={isEditBlockDialogOpen}
        onClose={handleCloseEditBlockDialog}
        block={selectedUnavailableBlock}
        practitioners={practitioners}
        onBlockUpdated={handleCalendarUpdated}
        onBlockDeleted={handleCalendarUpdated}
      />

      {/* Appointment edit dialog */}
      <AppointmentEditDialog
        open={isAppointmentEditDialogOpen}
        onClose={() => setIsAppointmentEditDialogOpen(false)}
        appointment={selectedAppointment}
        practitioners={practitioners}
        appointmentTypes={appointmentTypes}
        onAppointmentUpdated={handleCalendarUpdated}
      />
    </Box>
  );
};

export default AppointmentCalendar;